import { FC, useEffect, useRef } from "react";
import { FormInstance, notification } from "antd";
import useEdit, { BUSINESS_TRIP_REPORTS_STATUS } from "./hook";
import EditPage from "@lhd/components/CloneApp/pages/EditPage";
import ModalConfirm from "@lhd/components/Modal/ModalConfirm";
import Button from "@lhd/components/Button";
import { Styles } from "./styled";
import { ROUTES } from "@lhd/constants";
import { useSelector } from "react-redux";
import { user } from "@lhd/features/users/selector";
import { useNavigate } from "react-router-dom";

// Excluded fields from PDF export
const EXCLUDED_PDF_FIELD_NAMES = [
  "ステータス",
  "カテゴリー",
  "申請日",
  "承認日",
  "精算対象月",
];

const EditLayout = (props: ReturnType<typeof useEdit>) => {
  const {
    openConfirmModal,
    currentStatus,
    isOwner,
    isEditMode,
    isCurrentUserCreated,
    isApplicant,
    isAdmin,
    isSuperAdmin,
    recordDataRaw,
  } = props;
  const auth = useSelector(user);
  const navigate = useNavigate();
  const formRef = useRef<FormInstance<any> | null>(null);

  const isCurrentUserCreatorOrApplicant = isCurrentUserCreated || isApplicant;

  const endDatePart = props.dataAppRaw?.app_parts?.find(
    (part: any) => part.name === "終了日" || part.label_text === "終了日",
  );

  const handleConfirmSubmit = () => {
    const endDateValue = formRef.current?.getFieldValue(
      String(endDatePart?.id || ""),
    );
    if (!endDateValue) {
      notification.error({
        message:
          "終了日が未入力のため、申請できません。\n終了日を入力してください。",
      });
      return;
    }
    openConfirmModal(BUSINESS_TRIP_REPORTS_STATUS.SUBMITTED);
  };

  const extendsButtonRight = (
    _: any,
    hasChanged: boolean,
    options?: { isEditing?: boolean },
  ) => {
    if (!currentStatus) return <></>;

    const isEdit = options?.isEditing ?? isEditMode;

    if (isOwner) {
      if (currentStatus === BUSINESS_TRIP_REPORTS_STATUS.DRAFTING) {
        return (
          <Button
            label="申請"
            onClick={handleConfirmSubmit}
            disabled={
              hasChanged ||
              (!isCurrentUserCreatorOrApplicant && !isSuperAdmin) ||
              !isEdit
            }
          />
        );
      } else if (currentStatus === BUSINESS_TRIP_REPORTS_STATUS.SUBMITTED) {
        return (
          <Button
            label="取り下げ"
            onClick={() =>
              openConfirmModal(BUSINESS_TRIP_REPORTS_STATUS.DRAFTING, "owner")
            }
            disabled={
              hasChanged ||
              (!isCurrentUserCreatorOrApplicant && !isSuperAdmin) ||
              !isEdit
            }
          />
        );
      }
      return <></>;
    } else {
      // is manager
      const isManagerOrAdminOrSuperAdmin =
        isAdmin ||
        isSuperAdmin ||
        (recordDataRaw?.data as any)?.current_user_is_manager;
      return (
        <>
          <Button
            label="申請受付済（確認中）"
            onClick={() =>
              openConfirmModal(BUSINESS_TRIP_REPORTS_STATUS.REVIEWING)
            }
            disabled={
              !(currentStatus === BUSINESS_TRIP_REPORTS_STATUS.SUBMITTED) ||
              !isEdit ||
              !isManagerOrAdminOrSuperAdmin
            }
          />
          <Button
            label="承認済（給与明細反映）"
            onClick={() =>
              openConfirmModal(BUSINESS_TRIP_REPORTS_STATUS.APPROVED)
            }
            disabled={
              !(
                currentStatus === BUSINESS_TRIP_REPORTS_STATUS.SUBMITTED ||
                currentStatus === BUSINESS_TRIP_REPORTS_STATUS.REVIEWING
              ) ||
              !isEdit ||
              !isManagerOrAdminOrSuperAdmin
            }
          />
          <Button
            label="修正依頼"
            onClick={() =>
              openConfirmModal(
                BUSINESS_TRIP_REPORTS_STATUS.DRAFTING,
                "approval",
              )
            }
            disabled={
              !(
                currentStatus === BUSINESS_TRIP_REPORTS_STATUS.SUBMITTED ||
                currentStatus === BUSINESS_TRIP_REPORTS_STATUS.REVIEWING
              ) ||
              !isEdit ||
              !isManagerOrAdminOrSuperAdmin
            }
          />
          <Button
            label="却下"
            onClick={() =>
              openConfirmModal(BUSINESS_TRIP_REPORTS_STATUS.REJECTED)
            }
            disabled={
              !(
                currentStatus === BUSINESS_TRIP_REPORTS_STATUS.SUBMITTED ||
                currentStatus === BUSINESS_TRIP_REPORTS_STATUS.REVIEWING
              ) ||
              !isEdit ||
              !isManagerOrAdminOrSuperAdmin
            }
          />
        </>
      );
    }
  };

  useEffect(() => {
    if (!auth?.can_approve_business_trip_report && !isOwner) {
      navigate(`/${ROUTES.NOT_FOUND_PAGE}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth?.can_approve_business_trip_report, isOwner]);

  return (
    <Styles>
      <EditPage
        {...props}
        showPrintButton
        extendsButtonRight={extendsButtonRight}
        getInstanceForm={(form) => {
          formRef.current = form;
        }}
        hasControlPageMode
        excludePdfFieldNames={EXCLUDED_PDF_FIELD_NAMES}
      />
      <ModalConfirm
        isSubmitting={props.isLoadingChangeStatus}
        isOpen={props.openModalChangeStatus}
        content={props.modalContent}
        onCancel={() => props.setOpenModalChangeStatus(false)}
        onOk={props.onOkModalChangeStatus}
        width={480}
      />
    </Styles>
  );
};

const Edit: FC = () => <EditLayout {...useEdit()} />;

export default Edit;
